import { createFileRoute } from "@tanstack/react-router";
import { LocationPageTemplate, type LocationVariant } from "@/components/LocationPageTemplate";
import { getLocationCity } from "@/data/location-cities";
import { canonical } from "@/lib/seo";

const PATH = "/web-design-montclair-ca";
const TITLE = "Web Design Montclair CA | SimpleTech – (909) 255-9112";
const DESCRIPTION =
  "Need web design in Montclair, CA? SimpleTech Helper builds fast, mobile-friendly, SEO-ready websites for local small businesses. Call (909) 255-9112.";

export const Route = createFileRoute("/web-design-montclair-ca")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "web design Montclair CA, small business website design Montclair, Montclair web designer, SEO web design Inland Empire, (909) 255-9112",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical(PATH) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "geo.region", content: "US-CA" },
      { name: "geo.placename", content: "Montclair" },
      { name: "geo.position", content: "34.0775;-117.6897" },
      { name: "ICBM", content: "34.0775, -117.6897" },
    ],
    links: [{ rel: "canonical", href: canonical(PATH) }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "SimpleTech Helper",
          address: {
            "@type": "PostalAddress",
            streetAddress: "8880 N Benson Ave #121",
            addressLocality: "Montclair",
            addressRegion: "CA",
            postalCode: "91763",
            addressCountry: "US",
          },
          telephone: "(909) 255-9112",
          url: "https://simpletechhelper.com",
          areaServed: "Montclair CA, Inland Empire",
          serviceType: "Small Business Website Design",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: WebDesignMontclairPage,
});

const FAQS = [
  {
    q: "How much does small business web design cost in Montclair?",
    a: "Pricing depends on pages and features. Most Montclair small business sites land between a few hundred and a few thousand dollars depending on scope. Call (909) 255-9112 for a free, written quote.",
  },
  {
    q: "How long does it take to build a website for a Montclair business?",
    a: "Starter and Growth sites typically launch in 3–6 weeks. Full Service builds and e-commerce sites usually take 6–10 weeks, depending on copy and integration timelines.",
  },
  {
    q: "Will my Montclair website rank on Google?",
    a: "Yes. Every site we build includes on-page SEO, schema markup, fast load times, and Montclair- and Inland Empire-focused keyword targeting from day one.",
  },
  {
    q: "Can you redesign my existing Montclair business website?",
    a: "Yes. We modernize Montclair sites without losing existing Google rankings or traffic. We audit your current site, 301-map old URLs, and preserve the equity you've already built.",
  },
  {
    q: "Do I own my website when SimpleTech Helper builds it?",
    a: "Yes. The domain, hosting login, and source files all stay with you. No lock-in, no held-hostage hosting fees, no surprises if you ever move.",
  },
  {
    q: "Do you handle website hosting and maintenance for Montclair businesses?",
    a: "Yes. Hosting, SSL, malware scanning, uptime monitoring, security patches, and monthly content updates are all available as ongoing maintenance plans.",
  },
  {
    q: "Can you make my Montclair website ADA-compliant?",
    a: "Yes. We offer WCAG 2.1 AA audits and remediation to reduce legal risk and open your site to more customers. See our ADA-compliant website service for details.",
  },
  {
    q: "Is SimpleTech Helper based in Montclair?",
    a: "Yes — our office is at 8880 N Benson Ave #121, Montclair, CA 91763. We know the local market, meetings are quick, and we show up when you need us.",
  },
];

const INTRO = (
  <>
    <p>
      Your website is the first impression most Montclair customers ever have of your business —
      and if it's slow, outdated, or hard to find on Google, you're losing them before they ever
      call.{" "}
      <strong className="text-[#0b0a3d]">SimpleTech Helper</strong> delivers{" "}
      <strong className="text-[#0b0a3d]">web design Montclair CA</strong> small businesses rely on
      to compete locally: sites that rank, convert, and actually represent your brand.
    </p>
    <p>
      We're based at{" "}
      <strong className="text-[#0b0a3d]">8880 N Benson Ave #121, Montclair, CA 91763</strong>, so we
      know the local market, we show up when you need us, and we explain everything in plain
      English. No rushing. No jargon. You own the domain, the hosting login, and the source files
      when we're done — nothing is held hostage.
    </p>
    <p>
      Ready for a free consultation? Call{" "}
      <a href="tel:+19092559112" className="text-sky-600 font-bold hover:underline whitespace-pre-line">
        {"\n"}(909) 255-9112
      </a>{" "}
      or schedule a free 30-minute review of your current site.
    </p>
  </>
);

const VARIANT: LocationVariant = {
  serviceLabel: "Web Design",
  serviceLabelShort: "Web Design",
  parentHref: "/services/website-design",
  parentLabel: "Web Design",
  learnMoreText: "EXPLORE OUR WEB DESIGN SERVICES",
  nearMeEyebrow: "Web Design Near Me",
  whyTitle: "What Makes Our Web Design Different for Montclair, CA Businesses",
  expertHeroTitle:
    "Small Business Web Design in Montclair, CA — Fast, Local, and Built to Rank",
  expertNearMeTitle: "Trusted Web Design in Montclair, CA",
  introStrong: "Web Design Montclair CA",
  introBody: INTRO,
  faqHeading: (
    <>Frequently Asked<br />Questions About<br />Web Design in Montclair</>
  ),
  ctaTitle: "Ready for a Website That Actually Works for Your Montclair Business?",
  blogEyebrow: "From the Blog",
  blogTitle: "Web Design & SEO Tips for Montclair Businesses",
  faqs: FAQS,
};

function WebDesignMontclairPage() {
  const city = getLocationCity("montclair")!;
  return <LocationPageTemplate city={city} variant={VARIANT} />;
}
